import React, { useState } from "react";
import useTabId from "hooks/dashboard/useTabId";
import { T } from "services/lang/LanguageService";
import { DashboardLayoutType, IDashboardComponent } from "api/model/dashboard/DashboardComponent";
import useDashboardStore from "store/dashboard-store";
import { v4 as uuidv4 } from "uuid";
import { easasConfirmDialog } from "components/fragments/easas-global-fragment/EasasGlobalConfirmDialog";
import DashboardComponentCard from "./GroupComponentCard";
import DashboardComponentConfiguratorNew, { ConfiguratorModes } from "./GroupComponentConfiguratorNew";
import { updateDashboardTabs } from "../../utils";
import MoveGroupOrComponent from "./MoveGroupOrComponent";
import { ContextMenuItem } from "../layouts/interfaces";

interface DashboardComponentProps {
  groupId: string;
  component: IDashboardComponent;
  layoutType: DashboardLayoutType;
  isEditMode: boolean;
  setIsGridLayoutEditAvailable?: (isGridLayoutEditAvailable: boolean) => void;
  setCurrentContextMenuItems: (currentContextMenuItems: ContextMenuItem[]) => void;
}

enum DialogType {
  MOVE = "MOVE",
  EDITOR = "EDITOR",
}

const DashboardComponent = ({
  groupId,
  component,
  layoutType,
  isEditMode,
  setIsGridLayoutEditAvailable,
  setCurrentContextMenuItems,
}: DashboardComponentProps) => {
  const dashboard = useDashboardStore(state => state.dashboard);
  const updateComponent = useDashboardStore(state => state.updateComponent);
  const deleteComponent = useDashboardStore(state => state.removeComponent);
  const tabId = useTabId();
  const [isConfiguratorVisible, setIsConfiguratorVisible] = useState<boolean>(false);
  const [isMoveComponentVisible, setIsMoveComponentVisible] = useState<boolean>(false);

  const handleDeleteComponent = () => {
    easasConfirmDialog({
      icon: "pi pi-exclamation-triangle",
      header: T("action.confirm-action"),
      message: T("confirm-dialog.are-you-sure-you-want-to-delete-this-component"),
      confirmedCallback: () => {
        deleteComponent(groupId, component.id, tabId);
      },
    });
  };

  const handleDuplicateComponent = () => {
    if (!dashboard) return;
    const currentGroup = dashboard.dashboardTabsJs?.[tabId]?.componentGroups?.find(group => group.id === groupId);
    const componentToDuplicate = currentGroup?.components?.find(currentComponent => currentComponent.id === component.id);

    if (!currentGroup || !componentToDuplicate) return;

    const componentToDuplicateWithNewId = JSON.parse(JSON.stringify(componentToDuplicate));
    componentToDuplicateWithNewId.id = uuidv4();

    if (componentToDuplicateWithNewId.type === "Scada") {
      componentToDuplicateWithNewId.config.id = uuidv4();
    }

    currentGroup.components.push(componentToDuplicateWithNewId);

    const updatedTabs = dashboard.dashboardTabsJs.map((tab, index) =>
      index === tabId
        ? {
            ...tab,
            componentGroups: tab.componentGroups.map(group => (group.id === groupId ? currentGroup : group)),
          }
        : tab
    );

    updateDashboardTabs(dashboard, updatedTabs);
  };

  const items = [
    {
      label: T("action.edit-config"),
      icon: "pi pi-pencil",
      command: () => {
        handleDialogVisibilityChange(true, DialogType.EDITOR);
      },
    },
    {
      label: T("action.duplicate-component"),
      icon: "pi pi-clone",
      command: () => {
        easasConfirmDialog({
          icon: "pi pi-clone",
          acceptClassName: "p-button-success",
          header: T("action.confirm-action"),
          message: T("confirm-dialog.are-you-sure-you-want-to-duplicate-this-component"),
          confirmedCallback: () => {
            handleDuplicateComponent();
          },
        });
      },
    },
    {
      label: T("action.delete"),
      icon: "pi pi-times",
      command: () => {
        handleDeleteComponent();
      },
    },
    {
      label: T("action.move-component"),
      icon: "pi pi-arrow-right",
      command: () => {
        handleDialogVisibilityChange(true, DialogType.MOVE);
      },
    },
  ];

  const handleComponentChange = (newConfig: IDashboardComponent) => {
    updateComponent(groupId, component.id, newConfig, tabId);
    handleDialogVisibilityChange(false, DialogType.EDITOR);
  };

  const handleDialogVisibilityChange = (isVisible: boolean, dialogType: DialogType) => {
    if (dialogType === DialogType.MOVE) {
      setIsMoveComponentVisible(isVisible);
    } else {
      setIsConfiguratorVisible(isVisible);
    }
    if (setIsGridLayoutEditAvailable) setIsGridLayoutEditAvailable(!isVisible);
  };

  const handleOnContextMenu = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.preventDefault();
    setCurrentContextMenuItems(items);
  };

  return (
    <div className="h-full">
      <div className="h-full" onContextMenu={e => handleOnContextMenu(e)}>
        <DashboardComponentCard component={component} tabId={tabId} groupId={groupId} isEditMode={isEditMode} />
      </div>
      {isConfiguratorVisible && (
        <DashboardComponentConfiguratorNew
          isDialogVisible={isConfiguratorVisible}
          mode={ConfiguratorModes.EDIT}
          setIsDialogVisible={(isDialogVisible: boolean) => handleDialogVisibilityChange(isDialogVisible, DialogType.EDITOR)}
          component={component}
          onComponentChange={handleComponentChange}
          layoutType={layoutType}
        />
      )}
      {isMoveComponentVisible && dashboard && (
        <MoveGroupOrComponent
          isVisible={isMoveComponentVisible}
          setIsVisible={(isVisible: boolean) => handleDialogVisibilityChange(isVisible, DialogType.MOVE)}
          component={component}
          tabId={tabId}
          groupId={groupId}
          componentId={component.id}
          dashboardId={dashboard.id}
          isComponent
        />
      )}
    </div>
  );
};

function areEqual(prev: Readonly<DashboardComponentProps>, next: Readonly<DashboardComponentProps>): boolean {
  return (
    JSON.stringify(prev.component) === JSON.stringify(next.component) &&
    prev.groupId === next.groupId &&
    prev.layoutType === next.layoutType
  );
}

export default React.memo(DashboardComponent, areEqual);
